/**
 * EventClipsPage - Video clips captured during events (ADAS/DMS alarms, overspeed, etc.)
 *
 * Features:
 * - Device selector (only devices with camera)
 * - Date range picker
 * - Clips list with status badges and actions
 * - Video player for READY clips
 * - Retry for ERROR clips
 *
 * Route: /camera/clips or /camera/clips/:deviceId
 */

import {
  useState, useCallback, useMemo,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Stack,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogContent,
  DialogTitle,
  Card,
  CardContent,
  CardActions,
  Button,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';
import ReplayIcon from '@mui/icons-material/Replay';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import VideocamIcon from '@mui/icons-material/Videocam';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SpeedIcon from '@mui/icons-material/Speed';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FaceIcon from '@mui/icons-material/Face';

import useEventClips from '../hooks/useEventClips';
import { CHANNEL_NAMES } from '../utils/cameraConstants';

// ============================================
// Styled Components
// ============================================

const PageContainer = styled(Box)(({ theme }) => ({
  height: '100dvh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.primary,
  paddingBottom: 'env(safe-area-inset-bottom, 0px)',
}));

const Header = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  borderRadius: 0,
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const ContentArea = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(1),
  overflow: 'auto',
  gap: theme.spacing(1.5),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(2),
    gap: theme.spacing(2),
  },
}));

const Section = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(2),
  },
}));

const ClipCard = styled(Card)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
}));

// ============================================
// Constants
// ============================================

const STATUS_CONFIG = {
  pending: { label: 'Pendente', color: 'default' },
  recording: { label: 'Gravando', color: 'warning' },
  ready: { label: 'Pronto', color: 'success' },
  error: { label: 'Erro', color: 'error' },
};

const EVENT_TYPE_ICONS = {
  // ADAS (0x64)
  forwardCollision: { icon: VisibilityIcon, label: 'Colisao frontal' },
  laneDeparture: { icon: VisibilityIcon, label: 'Saida de faixa' },
  vehicleTooClose: { icon: VisibilityIcon, label: 'Veiculo proximo' },
  pedestrianCollision: { icon: VisibilityIcon, label: 'Pedestre' },
  frequentLaneChange: { icon: VisibilityIcon, label: 'Troca frequente de faixa' },
  obstacleDetection: { icon: VisibilityIcon, label: 'Obstaculo' },
  roadSignOverrun: { icon: VisibilityIcon, label: 'Placa ultrapassada' },
  // DMS (0x65)
  fatigueDriving: { icon: FaceIcon, label: 'Fadiga' },
  phoneCall: { icon: FaceIcon, label: 'Celular' },
  smoking: { icon: FaceIcon, label: 'Fumando' },
  distractedDriving: { icon: FaceIcon, label: 'Distracao' },
  abnormalDriving: { icon: FaceIcon, label: 'Direcao anormal' },
  driverAbnormality: { icon: FaceIcon, label: 'Motorista anomalia' },
  safetyBelt: { icon: FaceIcon, label: 'Cinto de seguranca' },
  cameraOcclusion: { icon: VisibilityIcon, label: 'Camera obstruida' },
  // G-Sensor (0x70)
  hardAcceleration: { icon: SpeedIcon, label: 'Aceleracao brusca' },
  hardBraking: { icon: SpeedIcon, label: 'Frenagem brusca' },
  hardCornering: { icon: SpeedIcon, label: 'Curva brusca' },
  accident: { icon: SpeedIcon, label: 'Colisao/Acidente' },
  // Other
  deviceOverspeed: { icon: SpeedIcon, label: 'Excesso de velocidade' },
};

// ============================================
// Helpers
// ============================================

const parseEventType = (eventType) => {
  if (!eventType) return { type: 'unknown', alarm: null };
  if (eventType.startsWith('alarm:')) {
    return { type: 'alarm', alarm: eventType.substring(6) };
  }
  return { type: eventType, alarm: null };
};

const getEventLabel = (eventType) => {
  const { alarm, type } = parseEventType(eventType);
  if (alarm && EVENT_TYPE_ICONS[alarm]) {
    return EVENT_TYPE_ICONS[alarm].label;
  }
  if (EVENT_TYPE_ICONS[type]) {
    return EVENT_TYPE_ICONS[type].label;
  }
  return eventType || 'Evento';
};

const getEventIcon = (eventType) => {
  const { alarm, type } = parseEventType(eventType);
  if (alarm && EVENT_TYPE_ICONS[alarm]) {
    return EVENT_TYPE_ICONS[alarm].icon;
  }
  if (EVENT_TYPE_ICONS[type]) {
    return EVENT_TYPE_ICONS[type].icon;
  }
  return WarningAmberIcon;
};

const formatDateTime = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const formatDuration = (seconds) => {
  if (!seconds) return '-';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m${s}s` : `${s}s`;
};

// ============================================
// Component
// ============================================

const EventClipsPage = () => {
  const { deviceId: paramDeviceId } = useParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const devicesMap = useSelector((state) => state.devices.items);

  // State
  const [deviceId, setDeviceId] = useState(paramDeviceId ? Number(paramDeviceId) : '');
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d;
  });
  const [toDate, setToDate] = useState(() => new Date());
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTriggered, setSearchTriggered] = useState(!!paramDeviceId);
  const [playerClip, setPlayerClip] = useState(null);

  // Fetch clips
  const {
    clips, loading, error, refresh, retryClip, deleteClip,
  } = useEventClips(
    searchTriggered ? deviceId : null,
    searchTriggered ? fromDate : null,
    searchTriggered ? toDate : null,
    statusFilter || null,
  );

  // Device with camera
  const device = useMemo(
    () => (deviceId ? devicesMap[deviceId] : null),
    [devicesMap, deviceId],
  );

  const devicesWithCamera = useMemo(
    () => Object.values(devicesMap || {}).filter((d) => d.attributes?.cameraEnabled),
    [devicesMap],
  );

  const handleSearch = useCallback(() => {
    if (deviceId) {
      setSearchTriggered(true);
      refresh();
    }
  }, [deviceId, refresh]);

  const handleRetry = useCallback(async (clipId) => {
    try {
      await retryClip(clipId);
    } catch {
      // error handled by hook
    }
  }, [retryClip]);

  const handleDelete = useCallback(async (clipId) => {
    try {
      await deleteClip(clipId);
    } catch {
      // error handled by hook
    }
  }, [deleteClip]);

  // ============================================
  // Render
  // ============================================

  return (
    <PageContainer>
      {/* Header */}
      <Header elevation={0}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <IconButton size="small" onClick={() => navigate('/camera')}>
            <ArrowBackIcon />
          </IconButton>
          <VideocamIcon color="primary" />
          <Typography variant="subtitle1" fontWeight={600} noWrap>
            Event Clips
          </Typography>
          {device && (
            <Chip
              size="small"
              label={device.name}
              variant="outlined"
            />
          )}
        </Stack>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Atualizar">
            <IconButton size="small" onClick={refresh} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : <SearchIcon />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Header>

      <ContentArea>
        {/* Filters */}
        <Section elevation={0}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack
              direction={isMobile ? 'column' : 'row'}
              spacing={1.5}
              alignItems={isMobile ? 'stretch' : 'center'}
            >
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Dispositivo</InputLabel>
                <Select
                  value={deviceId}
                  onChange={(e) => { setDeviceId(e.target.value); setSearchTriggered(false); }}
                  label="Dispositivo"
                >
                  {devicesWithCamera.map((d) => (
                    <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <DateTimePicker
                label="De"
                value={fromDate}
                onChange={setFromDate}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 180 } } }}
                ampm={false}
                format="dd/MM/yyyy HH:mm"
              />
              <DateTimePicker
                label="Ate"
                value={toDate}
                onChange={setToDate}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 180 } } }}
                ampm={false}
                format="dd/MM/yyyy HH:mm"
              />

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="pending">Pendente</MenuItem>
                  <MenuItem value="recording">Gravando</MenuItem>
                  <MenuItem value="ready">Pronto</MenuItem>
                  <MenuItem value="error">Erro</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={!deviceId || loading}
                size="small"
              >
                Buscar
              </Button>
            </Stack>
          </LocalizationProvider>
        </Section>

        {/* Error */}
        {error && (
          <Alert severity="error" onClose={() => {}}>{error}</Alert>
        )}

        {/* Results */}
        {searchTriggered && !loading && clips.length === 0 && (
          <Alert severity="info">Nenhum clip encontrado para o periodo selecionado.</Alert>
        )}

        {/* Clips Grid */}
        {clips.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                lg: 'repeat(3, 1fr)',
              },
              gap: 1.5,
            }}
          >
            {clips.map((clip) => {
              const EventIcon = getEventIcon(clip.eventType);
              const statusCfg = STATUS_CONFIG[clip.status] || STATUS_CONFIG.error;

              return (
                <ClipCard key={clip.id} variant="outlined">
                  <CardContent sx={{ pb: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <EventIcon fontSize="small" color="action" />
                        <Typography variant="body2" fontWeight={600}>
                          {getEventLabel(clip.eventType)}
                        </Typography>
                      </Stack>
                      <Chip
                        size="small"
                        label={statusCfg.label}
                        color={statusCfg.color}
                        sx={clip.status === 'recording' ? {
                          animation: 'pulse 1.5s ease-in-out infinite',
                          '@keyframes pulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.5 },
                          },
                        } : undefined}
                      />
                    </Stack>

                    <Stack spacing={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(clip.startTime)}
                        {' '}
                        &bull;
                        {' '}
                        CH
                        {clip.channel}
                        {' '}
                        (
                        {CHANNEL_NAMES[clip.channel] || `Canal ${clip.channel}`}
                        )
                        {' '}
                        &bull;
                        {' '}
                        {formatDuration(clip.duration)}
                      </Typography>
                      {clip.speed > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {Math.round(clip.speed * 1.852)}
                          {' '}
                          km/h
                        </Typography>
                      )}
                      {clip.retryCount > 0 && (
                        <Typography variant="caption" color="warning.main">
                          Tentativas:
                          {' '}
                          {clip.retryCount}
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>

                  <CardActions sx={{ pt: 0, px: 2, pb: 1.5 }}>
                    {clip.status === 'ready' && clip.clipUrl && (
                      <>
                        <Tooltip title="Reproduzir">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => setPlayerClip(clip)}
                          >
                            <PlayArrowIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            component="a"
                            href={`/api/events/clips/${clip.id}/download`}
                            target="_blank"
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {clip.status === 'error' && (
                      <Tooltip title="Tentar novamente">
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => handleRetry(clip.id)}
                        >
                          <ReplayIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {clip.status === 'recording' && (
                      <Chip
                        size="small"
                        icon={<CircularProgress size={12} />}
                        label="Gravando..."
                        variant="outlined"
                        color="warning"
                      />
                    )}
                    {clip.status === 'pending' && (
                      <Chip
                        size="small"
                        label="Aguardando..."
                        variant="outlined"
                      />
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(clip.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </ClipCard>
              );
            })}
          </Box>
        )}
      </ContentArea>

      {/* Video Player Dialog */}
      <Dialog
        open={!!playerClip}
        onClose={() => setPlayerClip(null)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        {playerClip && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {getEventLabel(playerClip.eventType)}
                {' '}
                &mdash; CH
                {playerClip.channel}
                {' '}
                {formatDateTime(playerClip.startTime)}
              </Typography>
              <IconButton onClick={() => setPlayerClip(null)} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <video
                src={playerClip.clipUrl}
                controls
                autoPlay
                style={{ width: '100%', maxHeight: '70vh', backgroundColor: '#000' }}
              />
            </DialogContent>
          </>
        )}
      </Dialog>
    </PageContainer>
  );
};

export default EventClipsPage;
